import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Rearranging a list or a grid by dragging
 * -------------------------------------------------------------
 * For the skill cards on the overview, the canvas rail and the Miro panel
 * (Huanrui, 2026-10-02): press, move a few pixels, and the card lifts and
 * follows the pointer while the others slide out of its way; let go and it
 * settles into its new place. A press that does not move is still a click.
 *
 * Hand-written rather than a library: pointer events and the FLIP technique
 * (measure where things were, let them move, then animate from the old place
 * to the new) are all it takes, for one kind of list.
 *
 * Applied to a WRAPPER around each card, never the card itself, so the
 * card's own hover tilt and glow -- which live in its transform -- are left
 * alone. Every measurement is taken from the screen at the moment it is
 * needed, so a list that scrolls, inside a page that scrolls, still works.
 */

const THRESHOLD = 5
const SETTLE_MS = 220
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

interface Point {
  x: number
  y: number
}

interface Session {
  id: string
  pointer: Point
  start: Point
  /** Where in the card it was picked up, from its top left. */
  grab: Point
  started: boolean
  /** The order when the drag began, for a drag that ends outside. */
  original: string[]
  order: string[]
  outside: boolean
}

export interface DragOrderOptions {
  ids: string[]
  /** Called once, when a drag inside ends with the order changed. */
  onReorder: (ids: string[]) => void
  /** Whether a point (client coordinates) is where the items can be rearranged. Everywhere when left out. */
  inside?: (x: number, y: number) => boolean
  /** While the pointer is outside, where it is; null when it comes back or lets go. */
  onOutside?: (id: string, at: Point | null) => void
  /** Let go outside. */
  onDropOutside?: (id: string, x: number, y: number) => void
  /** Nothing can be dragged while this is false. */
  enabled?: boolean
}

/** Where an element would be without the translate it is wearing right now. */
function home(element: HTMLElement): DOMRect {
  const box = element.getBoundingClientRect()
  const transform = getComputedStyle(element).transform
  if (!transform || transform === 'none') return box
  const matrix = new DOMMatrixReadOnly(transform)
  return new DOMRect(box.left - matrix.m41, box.top - matrix.m42, box.width, box.height)
}

export function useDragOrder({
  ids,
  onReorder,
  inside,
  onOutside,
  onDropOutside,
  enabled = true,
}: DragOrderOptions) {
  const [live, setLive] = useState<string[] | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [outside, setOutside] = useState(false)
  const elements = useRef(new Map<string, HTMLElement>())
  /** Where every other card was on screen just before a reorder. */
  const before = useRef<Map<string, DOMRect> | null>(null)
  const session = useRef<Session | null>(null)
  /** `end`, for `move`, which is made before it. */
  const endRef = useRef<((event: PointerEvent, cancelled: boolean) => void) | null>(null)
  const callbacks = useRef({ onReorder, inside, onOutside, onDropOutside })
  callbacks.current = { onReorder, inside, onOutside, onDropOutside }

  const order = live ?? ids

  /** The card in hand, drawn where the pointer has it. */
  const follow = useCallback(() => {
    const now = session.current
    if (!now || !now.started || now.outside) return
    const element = elements.current.get(now.id)
    if (!element) return
    element.style.transition = 'none'
    element.style.transform = ''
    const box = element.getBoundingClientRect()
    element.style.transform = `translate(${now.pointer.x - now.grab.x - box.left}px, ${now.pointer.y - now.grab.y - box.top}px)`
  }, [])

  // After a reorder has been drawn: every other card slides from where it was
  // to where it now is, and the one in hand stays under the pointer.
  useLayoutEffect(() => {
    const was = before.current
    before.current = null
    if (was) {
      const settling: HTMLElement[] = []
      for (const [id, element] of elements.current) {
        if (id === session.current?.id && !session.current.outside) continue
        const from = was.get(id)
        if (!from) continue
        element.style.transition = 'none'
        element.style.transform = ''
        const to = element.getBoundingClientRect()
        const dx = from.left - to.left
        const dy = from.top - to.top
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue
        element.style.transform = `translate(${dx}px, ${dy}px)`
        settling.push(element)
      }
      if (settling.length > 0) {
        void document.body.offsetWidth
        for (const element of settling) {
          element.style.transition = `transform ${SETTLE_MS}ms ${EASE}`
          element.style.transform = ''
        }
        window.setTimeout(() => {
          for (const element of settling) if (!element.style.transform) element.style.transition = ''
        }, SETTLE_MS + 40)
      }
    }
    follow()
  }, [order, follow])

  /** Note where everything is, then change the order. */
  const reorder = useCallback((next: string[]) => {
    const was = new Map<string, DOMRect>()
    for (const [id, element] of elements.current) was.set(id, element.getBoundingClientRect())
    before.current = was
    if (session.current) session.current.order = next
    setLive(next)
  }, [])

  const move = useCallback(
    (event: PointerEvent) => {
      const now = session.current
      if (!now) return
      // Let go somewhere this page never heard about -- outside the window,
      // over another app -- so no pointerup came. Moving with no button held
      // is the first sign of it: end the drag there, or the card stays
      // lifted and lit until the page is reloaded (2026-10-05).
      if (now.started && event.pointerType === 'mouse' && event.buttons === 0) {
        endRef.current?.(event, false)
        return
      }
      now.pointer = { x: event.clientX, y: event.clientY }

      if (!now.started) {
        if (Math.abs(now.pointer.x - now.start.x) <= THRESHOLD && Math.abs(now.pointer.y - now.start.y) <= THRESHOLD) {
          return
        }
        now.started = true
        document.body.style.userSelect = 'none'
        window.getSelection()?.removeAllRanges()
        setDragging(now.id)
        setLive(now.order)
      }

      const isInside = callbacks.current.inside ? callbacks.current.inside(event.clientX, event.clientY) : true
      if (!isInside) {
        if (!now.outside) {
          now.outside = true
          setOutside(true)
          // Back to where it began, the cards sliding home.
          const element = elements.current.get(now.id)
          if (element) {
            element.style.transition = `transform ${SETTLE_MS}ms ${EASE}`
            element.style.transform = ''
          }
          if (now.order.join('\n') !== now.original.join('\n')) reorder(now.original)
        }
        callbacks.current.onOutside?.(now.id, now.pointer)
        return
      }
      if (now.outside) {
        now.outside = false
        setOutside(false)
        callbacks.current.onOutside?.(now.id, null)
      }

      follow()

      // The nearest place to the middle of the card in hand.
      const element = elements.current.get(now.id)
      if (!element) return
      const held = element.getBoundingClientRect()
      const cx = held.left + held.width / 2
      const cy = held.top + held.height / 2
      let best = now.order.indexOf(now.id)
      let bestDistance = Infinity
      now.order.forEach((id, index) => {
        const other = elements.current.get(id)
        if (!other) return
        const box = id === now.id ? home(other) : home(other)
        const distance = Math.hypot(box.left + box.width / 2 - cx, box.top + box.height / 2 - cy)
        if (distance < bestDistance) {
          bestDistance = distance
          best = index
        }
      })
      const from = now.order.indexOf(now.id)
      if (best !== from && best >= 0) {
        const next = [...now.order]
        next.splice(from, 1)
        next.splice(best, 0, now.id)
        reorder(next)
      }
    },
    [follow, reorder],
  )

  const end = useCallback(
    (event: PointerEvent, cancelled: boolean) => {
      const now = session.current
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
      if (!now) return
      if (!now.started) {
        session.current = null
        return
      }
      document.body.style.userSelect = ''

      // The click that follows letting go is not a click on the card.
      const swallow = (click: MouseEvent) => {
        click.preventDefault()
        click.stopPropagation()
      }
      window.addEventListener('click', swallow, { capture: true, once: true })
      window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 80)

      const element = elements.current.get(now.id)
      if (element) {
        element.style.transition = `transform ${SETTLE_MS}ms ${EASE}`
        element.style.transform = ''
      }
      const id = now.id
      window.setTimeout(() => {
        if (element && !element.style.transform) element.style.transition = ''
        setDragging((current) => (current === id ? null : current))
      }, SETTLE_MS)

      if (now.outside) {
        callbacks.current.onOutside?.(now.id, null)
        if (!cancelled) callbacks.current.onDropOutside?.(now.id, event.clientX, event.clientY)
        setOutside(false)
        setLive(null)
      } else {
        const final = cancelled ? now.original : now.order
        if (final.join('\n') !== now.original.join('\n')) callbacks.current.onReorder(final)
        setLive(null)
      }
      session.current = null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [move],
  )
  // Named so they can be removed: the same function objects every time.
  const up = useCallback((event: PointerEvent) => end(event, false), [end])
  const cancel = useCallback((event: PointerEvent) => end(event, true), [end])
  endRef.current = end

  // The window losing the pointer altogether (switching apps, a dialog) puts
  // the card back where it was, rather than leaving it held.
  useEffect(() => {
    const away = () => {
      if (session.current) end(new PointerEvent('pointercancel'), true)
    }
    const hidden = () => {
      if (document.visibilityState === 'hidden') away()
    }
    window.addEventListener('blur', away)
    document.addEventListener('visibilitychange', hidden)
    return () => {
      window.removeEventListener('blur', away)
      document.removeEventListener('visibilitychange', hidden)
    }
  }, [end])

  const itemProps = useCallback(
    (id: string) => ({
      ref: (element: HTMLElement | null) => {
        if (element) elements.current.set(id, element)
        else elements.current.delete(id)
      },
      onPointerDown: (event: React.PointerEvent) => {
        if (!enabled || event.button !== 0 || session.current) return
        const target = event.target as HTMLElement
        if (target.closest('[data-nodrag], input, textarea, select')) return
        const element = elements.current.get(id)
        if (!element) return
        const box = element.getBoundingClientRect()
        session.current = {
          id,
          pointer: { x: event.clientX, y: event.clientY },
          start: { x: event.clientX, y: event.clientY },
          grab: { x: event.clientX - box.left, y: event.clientY - box.top },
          started: false,
          original: [...order],
          order: [...order],
          outside: false,
        }
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', up)
        window.addEventListener('pointercancel', cancel)
      },
      // A link or a picture inside would start the browser's own drag, which
      // takes the pointer away.
      onDragStart: (event: React.DragEvent) => event.preventDefault(),
      'data-drag': dragging !== null && dragging === id ? (outside ? 'out' : 'held') : undefined,
    }),
    [enabled, order, move, up, cancel, dragging, outside],
  )

  return { order, dragging, outside, itemProps }
}
